/**
 * WhatIfSimulator — Interactive scenario simulator (Inventory tab).
 *
 * Lets a stakeholder flex 4 levers:
 *   - Demand multiplier (1.0 / 1.25 / 1.5 / 2.0)
 *   - A hypothetical new retailer order
 *   - Production lead-time override
 *   - A warehouse-to-warehouse transfer
 *
 * All projections (stockout, supply gap, revenue at risk) are
 * recomputed by `useDistributionSimulation` so the three pages stay in
 * sync.
 */
import React from 'react';
import { Card } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import {
    Beaker, Zap, TrendingUp, Truck, Clock, RotateCcw, Package,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDistributionDemo } from '../DistributionDemoContext';

const DEMAND_PRESETS = [
    { value: 1.0, label: 'Baseline' },
    { value: 1.25, label: '+25%' },
    { value: 1.5, label: '+50%' },
    { value: 2.0, label: '+100%' },
];

const LEAD_TIME_PRESETS = [
    { value: null, label: 'Standard (56d)' },
    { value: 42, label: 'Priority (42d)' },
    { value: 28, label: 'Expedited (28d)' },
];

const formatEur = (n) => `€${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const ScenarioButton = ({ active, onClick, children, testid }) => (
    <button
        data-testid={testid}
        onClick={onClick}
        className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
            active
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
        }`}
    >
        {children}
    </button>
);

export default function WhatIfSimulator({ projections = [] }) {
    const {
        demandMultiplier, setDemandMultiplier,
        hypotheticalOrder, setHypotheticalOrder,
        leadTimeOverride, setLeadTimeOverride,
        warehouseTransfer, setWarehouseTransfer,
        resetSimulation,
        isSimulating,
        flashNodes,
    } = useDistributionDemo();

    const vnl = projections.find(p => p.sku === 'OTR-001-VNL');

    const applyHypoOrder = () => {
        setHypotheticalOrder({ sku: 'OTR-001-VNL', qty: 500, retailer: 'New Retailer' });
        flashNodes(['retailers', 'warehouse']);
        toast.success('Simulated a new 500-unit retailer order');
    };

    const clearHypoOrder = () => setHypotheticalOrder(null);

    const applyTransfer = () => {
        setWarehouseTransfer({ sku: 'OTR-001-VNL', qty: 400, from: 'nordisk-no', to: 'gls-se' });
        flashNodes(['warehouse_alt', 'warehouse']);
        toast.success('Simulated transfer: 400 vinyl Oslo → Sweden');
    };

    const clearTransfer = () => setWarehouseTransfer(null);

    return (
        <Card
            className="border-indigo-200 bg-gradient-to-br from-indigo-50/60 to-white"
            data-testid="what-if-simulator"
        >
            <div className="p-5">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                    <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-md bg-indigo-600 text-white flex items-center justify-center flex-shrink-0">
                            <Beaker className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-base font-semibold text-slate-900">What-If Simulator</h3>
                                {isSimulating && (
                                    <Badge className="bg-indigo-600 animate-pulse">Scenario active</Badge>
                                )}
                            </div>
                            <p className="text-xs text-slate-600 mt-0.5">
                                Flex the network — projections across all three tabs update together.
                            </p>
                        </div>
                    </div>
                    {isSimulating && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={resetSimulation}
                            data-testid="simulator-reset"
                        >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Reset
                        </Button>
                    )}
                </div>

                <div className="grid lg:grid-cols-2 gap-4">
                    {/* Demand lever */}
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                            <TrendingUp className="h-3.5 w-3.5 text-rose-500" />
                            Consumer demand
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {DEMAND_PRESETS.map(p => (
                                <ScenarioButton
                                    key={p.value}
                                    active={demandMultiplier === p.value}
                                    onClick={() => { setDemandMultiplier(p.value); flashNodes(['consumers', 'retailers']); }}
                                    testid={`sim-demand-${p.value}`}
                                >
                                    {p.label}
                                </ScenarioButton>
                            ))}
                        </div>
                    </div>

                    {/* Lead time lever */}
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                            <Clock className="h-3.5 w-3.5 text-amber-500" />
                            Manufacturing lead time
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {LEAD_TIME_PRESETS.map(p => (
                                <ScenarioButton
                                    key={String(p.value)}
                                    active={leadTimeOverride === p.value}
                                    onClick={() => { setLeadTimeOverride(p.value); flashNodes(['manufacturer']); }}
                                    testid={`sim-lead-${p.value ?? 'standard'}`}
                                >
                                    {p.label}
                                </ScenarioButton>
                            ))}
                        </div>
                    </div>

                    {/* New retailer order */}
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                            <Package className="h-3.5 w-3.5 text-indigo-500" />
                            New retailer order (vinyl)
                        </div>
                        {hypotheticalOrder ? (
                            <div className="flex items-center justify-between">
                                <div className="text-sm">
                                    <strong>{hypotheticalOrder.qty}</strong> units queued from {hypotheticalOrder.retailer}
                                </div>
                                <Button size="sm" variant="ghost" onClick={clearHypoOrder} data-testid="sim-clear-order">
                                    Clear
                                </Button>
                            </div>
                        ) : (
                            <Button size="sm" onClick={applyHypoOrder} data-testid="sim-add-order">
                                Simulate a 500-unit order
                            </Button>
                        )}
                    </div>

                    {/* Warehouse transfer */}
                    <div className="rounded-md border border-slate-200 bg-white p-3">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                            <Truck className="h-3.5 w-3.5 text-emerald-500" />
                            Transfer between warehouses
                        </div>
                        {warehouseTransfer ? (
                            <div className="flex items-center justify-between">
                                <div className="text-sm">
                                    <strong>{warehouseTransfer.qty}</strong> vinyl · Oslo → Sweden
                                </div>
                                <Button size="sm" variant="ghost" onClick={clearTransfer} data-testid="sim-clear-transfer">
                                    Clear
                                </Button>
                            </div>
                        ) : (
                            <Button size="sm" onClick={applyTransfer} data-testid="sim-add-transfer">
                                <Truck className="h-3 w-3 mr-1" />
                                Transfer 400 from Oslo
                            </Button>
                        )}
                    </div>
                </div>

                {/* Live outcome for the exposed SKU */}
                {vnl && (
                    <div
                        className="mt-4 rounded-md border border-slate-200 bg-white p-3"
                        data-testid="simulator-outcome"
                    >
                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                            Live projection · {vnl.sku} {vnl.format}
                        </div>
                        <div className="grid sm:grid-cols-4 gap-3 text-sm">
                            <div>
                                <div className="text-xs text-slate-500">Days cover</div>
                                <div className="text-xl font-bold text-slate-900 tabular-nums" data-testid="outcome-days-cover">
                                    {vnl.daysCover ?? '—'}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Supply gap</div>
                                <div className={`text-xl font-bold tabular-nums ${vnl.supplyGapDays > 0 ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="outcome-supply-gap">
                                    {vnl.supplyGapDays > 0 ? `${vnl.supplyGapDays} d` : 'Closed'}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Units at risk</div>
                                <div className="text-xl font-bold text-slate-900 tabular-nums" data-testid="outcome-units-at-risk">
                                    {vnl.unitsAtRisk.toLocaleString()}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Revenue at risk</div>
                                <div className="text-xl font-bold text-slate-900 tabular-nums" data-testid="outcome-revenue-at-risk">
                                    {formatEur(vnl.revenueAtRisk)}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
}
